import React, { Fragment } from 'react';

import cn from 'classnames';
import { intersection, uniq, upperCase } from 'lodash';
import { MdViewList } from 'react-icons/md';
import { FormattedHTMLMessage, FormattedMessage } from 'react-intl';
import { Text } from 'scouting-ui/lib';

import { SHOW_VALIDATE_EAGLE } from '@config/config';
import { EditSubUnits } from '@modules/advancement/packRoster/components/PackRoster/EditSubUnits/EditSubUnits';
import {
  getIsDenChief,
  replacements,
} from '@modules/advancement/packRoster/utilsTyped';
import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import { PERMISSION_CATEGORIES } from '@modules/permissionManager/constants';
import { Space } from '@modules/shared/components/Space/Space';
import { getPreferredName } from '@modules/shared/utils/preferredName';
import { formatToUIDate } from '@modules/utils/datesLuxon';
import { denTypesSingleNames as denNames, memberTypeLabels } from '@shared';
import {
  Button,
  ConditionalRender,
  PersonAvatar,
  S,
  Spin,
  TableHeadingSort,
  Tag,
  Tooltip,
  devMockHighlightClassName,
} from '@shared/components';
import { PositionId, hidePositions, programNames } from '@shared/constants';
import { intl } from '@shared/localization';
import tableSortOrder from '@utils/tableSortOrder';

import { YOUTH_MEMBER, importantPositions } from '../../../constants';
import OtherRanksTooltip from '../OtherRanksTooltip';
import PersonWarning from '../PersonWarning';
import { RenewalStatusColumn } from './Columns';
import { OptoutColumn } from './OptoutColumn';

const columns = (
  {
    sorter,
    onGoToYouthProfile,
    onGoToAdultProfile,
    isSubUnitAllowed,
    subUnitType,
    approvedSubUnits,
    showSubUnits,
    canEditAllSubUnits,
    defaultSubUnitList,
    areRenewalsLoading,
    isInactiveUnit,
    orgIsPack,
    unitTypeId,
    hasSubUnitOnlyPerms,
    canEditAllYouthProfiles,
    canSeeYouthProfiles,
    canSeeLeaderProfiles,
    canSeeMySubunitProfiles,
    mySubUnits,
    organizationGuid: orgGuid,
    onSaveOptOut,
    onGroupByOrderStatus,
    unitRequiredRosterPositions,
    rosterAllPositions,
    subUnitRequiredRosterPositions,
  },
  styles,
  onClickSubUnitHeader,
  onClickTypeHeader,
) => {
  const handleSwitchOptout = (personGuid, registrationId) => value => {
    onSaveOptOut({ personGuid, optOutValue: value, registrationId });
  };

  const handleSwitchClick = e => e.stopPropagation();

  const getOptions = subUnitListIds => {
    const filtered = approvedSubUnits
      .filter(subUnit => subUnitListIds.find(id => id === subUnit.subUnitId))
      .map(subUnit => ({
        ...subUnit,
        subUnitName: denNames[subUnit.denType]
          ? denNames[subUnit.denType] === denNames.aol
            ? `${upperCase(denNames[subUnit.denType])} ${
                subUnit.subUnitNameRaw
              }`
            : `${denNames[subUnit.denType]} ${subUnit.subUnitNameRaw}`
          : subUnit.subUnitName,
      }));

    return filtered;
  };

  const getFormattedData = data => {
    const defaultValues = defaultSubUnitList.find(
      item => item.userId === data.userId,
    );
    let subUnitFound;
    if (defaultValues) {
      subUnitFound = approvedSubUnits.find(
        subUnit => subUnit.subUnitId === defaultValues.subUnitId,
      );
    }

    return {
      ...data,
      ...defaultValues,
      subUnitName: subUnitFound ? subUnitFound.subUnitName : data.subUnitName,
    };
  };

  const getCanViewProfile = (showSubUnits, data, formattedData) => {
    const canViewOnlyMySubUnitProfiles =
      canSeeMySubunitProfiles && !canEditAllYouthProfiles;
    if (canViewOnlyMySubUnitProfiles) {
      const dataToUse = showSubUnits ? data : formattedData;

      if (dataToUse.subUnitId === -1) return false;

      const hasMatchingSubUnits = mySubUnits.some(sub => {
        const mySubId = sub.denId || sub.patrolId;
        return mySubId === dataToUse.subUnitId;
      });
      return hasMatchingSubUnits;
    }

    return data.isAdult ? canSeeLeaderProfiles : canSeeYouthProfiles;
  };

  const validateEagleColumn = SHOW_VALIDATE_EAGLE
    ? [
        {
          render(_text, record) {
            if (record.currentHighestRankApproved.rank === 'Life') {
              return (
                <div className={styles.validateEagleWrapper}>
                  <Button
                    fitText
                    color="success"
                    onClick={e => e.stopPropagation()}
                  >
                    <FormattedMessage id="advancement.PackRoster.validateEagle" />
                  </Button>
                </div>
              );
            }
            return '';
          },
        },
      ]
    : [];

  const subUnitColumn =
    isSubUnitAllowed && approvedSubUnits.length > 0
      ? [
          {
            width: '18rem',
            title: (
              <div className={styles.heading} onClick={onClickSubUnitHeader}>
                <FormattedMessage
                  id={`advancement.PackRoster.column.${subUnitType}`}
                />
                <Tooltip
                  title={
                    <FormattedMessage id="advancement.PackRoster.column.subUnitMessage" />
                  }
                >
                  <MdViewList fontSize="small" />
                </Tooltip>
              </div>
            ),
            sorter: false,
            sortOrder: tableSortOrder(sorter, 'subUnitName'),
            dataIndex: 'subUnitName',
            render(subUnitName, data) {
              const formattedData = getFormattedData(data);

              // For users with more than 1 subunit
              if (formattedData?.subUnitListIds?.length > 1 && !showSubUnits) {
                const youthSubUnits = getOptions(formattedData.subUnitListIds);
                const displaySubUnit = youthSubUnits.shift();

                return (
                  <div>
                    <Tooltip
                      placement="bottom"
                      title={youthSubUnits.map(({ subUnitName, subUnitId }) => (
                        <Fragment key={subUnitId}>
                          <FormattedHTMLMessage
                            id="advancement.PackRoster.column.subunitTooptip"
                            values={{
                              subName: subUnitName,
                            }}
                          />
                          <br />
                        </Fragment>
                      ))}
                    >
                      <span className={styles.counterContainer}>
                        <span className={styles.counter}>
                          <FormattedMessage
                            id="advancement.PackRoster.column.subunitCount"
                            values={{
                              subunitCount: youthSubUnits.length,
                            }}
                          />
                        </span>
                      </span>
                    </Tooltip>
                    <span>{displaySubUnit.subUnitName}</span>
                  </div>
                );
              }

              return (
                <div className={styles.subUnitCel}>
                  <S size="4" className={styles.subUnitName}>
                    {subUnitName}
                  </S>
                  <EditSubUnits
                    data={data}
                    defaultSubUnitList={defaultSubUnitList}
                    approvedSubUnits={approvedSubUnits}
                    hasSubUnitOnlyPerms={hasSubUnitOnlyPerms}
                    canEditAllSubUnits={canEditAllSubUnits}
                    mySubUnits={mySubUnits}
                    showSubUnits={showSubUnits}
                  />
                </div>
              );
            },
          },
        ]
      : [];

  const renewalColumns = [
    {
      width: '15rem',
      title: (
        <Space size="sm">
          <ConditionalRender
            value={featureFlags.getFlag('SBL_5483_GROUPBY_ORDER_STATUS')}
          >
            <div
              onClick={onGroupByOrderStatus}
              className={styles.groupByOrderBtn}
            >
              {intl.formatMessage({
                id: 'advancement.PackRoster.column.renewalStatus',
              })}{' '}
              <MdViewList fontSize="small" />
            </div>
          </ConditionalRender>
          <ConditionalRender
            value={!featureFlags.getFlag('SBL_5483_GROUPBY_ORDER_STATUS')}
          >
            {intl.formatMessage({
              id: 'advancement.PackRoster.column.renewalStatus',
            })}{' '}
          </ConditionalRender>
        </Space>
      ),
      dataIndex: 'renewalStatus',
      render(renewalStatus, data) {
        return (
          <RenewalStatusColumn
            renewalStatus={renewalStatus}
            record={data}
            unitTypeId={unitTypeId}
          />
        );
      },
    },
    {
      width: '10ch',
      title: featureFlags.getFlag('SBL_5450_DROP_OUT')
        ? intl.formatMessage({
            id: 'advancement.PackRoster.column.dropping',
          })
        : intl.formatMessage({
            id: 'advancement.PackRoster.column.optOut',
          }),
      render(
        _,
        { renewalStatus, personGuid, registrationId, canOptOut, bsaOrderId },
      ) {
        return (
          <OptoutColumn
            renewalStatus={renewalStatus}
            bsaOrderId={bsaOrderId}
            registrationId={registrationId}
            handleSwitchOptout={handleSwitchOptout}
            handleSwitchClick={handleSwitchClick}
            personGuid={personGuid}
            areRenewalsLoading={areRenewalsLoading}
            canOptOut={canOptOut}
          />
        );
      },
    },
    {
      width: '14rem',
      sorter: true,
      sortOrder: tableSortOrder(sorter, 'registrationExpireDt'),
      title: (
        <TableHeadingSort name="registrationExpireDt" sorter={sorter}>
          <FormattedMessage id="advancement.PackRoster.column.registrationExpireDt" />
        </TableHeadingSort>
      ),
      dataIndex: 'registrationExpireDt',
      render(registrationExpireDt, record) {
        const { registrationId, isYearlyMembershipRenewalPaid } = record;

        if (isYearlyMembershipRenewalPaid)
          return (
            <FormattedMessage id="advancement.PackRoster.column.expirationDate.future" />
          );

        if (areRenewalsLoading && !registrationId) {
          return <Spin size="small" />;
        }

        if (!registrationExpireDt) return 'N/A';

        return formatToUIDate(registrationExpireDt);
      },
    },
  ];

  return [].concat(
    [
      {
        dataIndex: 'isAdult',
        width: '5rem',
        fixed: 'left',
        render(isAdult, { pictureUrl, gender }) {
          return (
            <PersonAvatar
              className={devMockHighlightClassName}
              isAdult={isAdult}
              gender={gender}
              pictureUrl={pictureUrl}
            />
          );
        },
      },
      {
        key: 'name',
        width: '20rem',
        fixed: 'left',
        sorter: true,
        sortOrder: tableSortOrder(sorter, 'name'),
        className: styles.userNameColumn,
        title: (
          <TableHeadingSort name="name" sorter={sorter}>
            <FormattedMessage id="advancement.PackRoster.column.name" />
          </TableHeadingSort>
        ),
        render(person, data) {
          const formattedData = getFormattedData(data);
          const { userId, isAdult } = person;
          const personPreferredName = getPreferredName(person);
          const showLinkClass = !!userId;

          if (
            isInactiveUnit ||
            !getCanViewProfile(showSubUnits, data, formattedData)
          ) {
            return (
              <S size="4" bold inlineBlock>
                {personPreferredName}
              </S>
            );
          }

          return (
            <>
              <S
                size="4"
                className={cn({
                  [styles.userName]: showLinkClass,
                })}
                onClick={
                  userId &&
                  (isAdult
                    ? onGoToAdultProfile(person)
                    : onGoToYouthProfile(person))
                }
                colored
                bold
              >
                {/* This anchor is to have open in a new tab right click menu */}
                <a
                  href={
                    isAdult
                      ? `/adultProfile/${userId}?organizationGuid=${orgGuid}`
                      : `/youthProfile/${userId}?organizationGuid=${orgGuid}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={e => e.preventDefault()}
                  className={styles.userNameLink}
                  title={personPreferredName}
                >
                  {personPreferredName}
                </a>
              </S>
              <PersonWarning person={person} />
            </>
          );
        },
      },
      {
        width: '10rem',
        title: intl.formatMessage({
          id: 'advancement.PackRoster.column.memberId',
        }),
        dataIndex: 'personId',
      },
      {
        width: '8rem',
        title: (
          <div className={styles.heading}>
            <FormattedMessage id="advancement.PackRoster.column.memberType" />
            <Tooltip
              title={
                <FormattedMessage id="advancement.PackRoster.column.typeMessage" />
              }
            >
              <MdViewList fontSize="small" onClick={onClickTypeHeader} />
            </Tooltip>
          </div>
        ),
        render(_, data) {
          let bgColor = data.age >= 18 ? '#515354' : 'green';
          let customStyle =
            data.age >= 18 ? styles.participantPill : styles.youthPill;
          let label =
            data.age >= 18
              ? memberTypeLabels.participant
              : memberTypeLabels.youth;
          // SBL-4178 V2 returns isAdult = true for youths endpoint
          if (data.isAdult && !data.isUnitParticipant) {
            bgColor = 'orange';
            customStyle = styles.adultPill;
            label =
              data.isParent && !data.isLeader
                ? memberTypeLabels.parent
                : memberTypeLabels.adult;
          }

          return (
            <Tag
              color={bgColor}
              outline={false}
              className={cn(styles.typePill, customStyle)}
            >
              <FormattedMessage id={`advancement.PackRoster.row.${label}`} />
            </Tag>
          );
        },
      },
      {
        width: '7rem',
        title: (
          <TableHeadingSort name="age" sorter={sorter}>
            <FormattedMessage id="advancement.PackRoster.column.age" />
          </TableHeadingSort>
        ),
        dataIndex: 'age',
        sorter: true,
        sortOrder: tableSortOrder(sorter, 'age'),
        render(age) {
          return age;
        },
      },
      {
        width: '18rem',
        key: 'currentHighestRankApproved',
        title: (
          <TableHeadingSort name="currentHighestRankApproved" sorter={sorter}>
            <FormattedMessage id="advancement.PackRoster.column.highestRankApproved" />
          </TableHeadingSort>
        ),
        sorter: true,
        sortOrder: tableSortOrder(sorter, 'currentHighestRankApproved'),
        render(_, data) {
          const {
            rosterCurrentHighestRankApproved = {},
            rosterOtherHighestRanksApproved: otherRanks,
          } = data;
          const isDenChief = getIsDenChief(data);
          return isDenChief && orgIsPack ? (
            <FormattedMessage id="shared.na" />
          ) : (
            <OtherRanksTooltip
              currentRank={rosterCurrentHighestRankApproved}
              otherRanks={otherRanks}
            />
          );
        },
      },
    ],
    subUnitColumn,
    validateEagleColumn,
    [
      {
        width: '14rem',
        title: intl.formatMessage({
          id: 'advancement.PackRoster.column.position',
        }),
        render(_, data) {
          const allwaysDisplay = importantPositions;
          const { positions = [] } = data;
          const isDenChief = getIsDenChief(data);
          const filteredPositions = positions.filter(
            pos => !hidePositions.includes(pos.position),
          );
          const positionsList =
            isDenChief && orgIsPack
              ? filteredPositions
                  .filter(pos => pos.positionId === PositionId.DenChief)
                  .map(pos => pos.position)
              : uniq(
                  filteredPositions.map(pos => pos.position).filter(pos => pos),
                );
          const mustDisplay = positionsList.filter(pos =>
            allwaysDisplay.includes(pos),
          );
          const minimalDisplay = mustDisplay.length
            ? mustDisplay
            : positionsList.length
            ? [positionsList[positionsList.length - 1]]
            : [];
          const toolTipPositions = positionsList
            .filter(pos => !minimalDisplay.includes(pos))
            .sort();

          const formattedDisplay = replacements.reduce(
            (str, [from, to]) => str.replace(from, to),
            minimalDisplay.toString(),
          );

          const unitRequiredPositionsRecord =
            unitRequiredRosterPositions?.[data.personGuid] ?? [];
          const positionsRecord = rosterAllPositions?.[data.personGuid] ?? [];
          const subUnitRequiredPositionsRecord =
            subUnitRequiredRosterPositions?.[data.personGuid] ?? [];
          const positionsPermissions = [
            ...positionsRecord,
            ...subUnitRequiredPositionsRecord,
            ...unitRequiredPositionsRecord,
          ];

          let categories = [];
          if (positionsPermissions.length) {
            categories = PERMISSION_CATEGORIES.filter(
              category =>
                intersection(category.positions, positionsPermissions)?.length,
            );
          }

          const permissionTooltipContent = categories.map(panel => (
            <Fragment key={panel.key}>
              <Text font="body-body-3" color="white">
                {panel.title}
              </Text>
              <ul className={styles.list}>
                {intersection(panel.positions, positionsPermissions).map(
                  item => (
                    <li key={`permPos-${item}`}>{item}</li>
                  ),
                )}
              </ul>
            </Fragment>
          ));

          return (
            <div>
              {toolTipPositions.length ? (
                <Tooltip
                  placement="bottom"
                  title={toolTipPositions.map(pos => (
                    <Fragment key={pos}>
                      <FormattedHTMLMessage
                        id="advancement.PackRoster.column.positionMessage"
                        values={{
                          position: pos.replaceAll(
                            programNames.BOY_SCOUT,
                            YOUTH_MEMBER,
                          ),
                        }}
                      />
                      <br />
                    </Fragment>
                  ))}
                >
                  <span className={styles.counterContainer}>
                    <span className={styles.counter}>
                      <FormattedMessage
                        id="advancement.PackRoster.column.positionCount"
                        values={{
                          count: toolTipPositions.length,
                        }}
                      />
                    </span>
                  </span>
                </Tooltip>
              ) : null}
              {categories.length ? (
                <Tooltip placement="top" title={permissionTooltipContent}>
                  <span className={styles.counterContainer}>
                    <span className={styles.counter}>
                      <FormattedMessage id="advancement.PackRoster.column.permissionsTooltip" />
                    </span>
                  </span>
                </Tooltip>
              ) : null}

              <span>{formattedDisplay}</span>
            </div>
          );
        },
      },
    ],
    renewalColumns,
  );
};

export default columns;
